/**
 * Constants and PDF generator for Estimate (Quotation Form)
 */
import { jsPDF } from "jspdf";
import { fileUrl } from "./api";
import { getCompanySettings, companyContactLines } from "./companySettings";
import { EN } from "../i18n/en";
import { ES } from "../i18n/es";
import { logError } from "./logger";

async function _loadLogoForEstimate(company) {
  if (!company?.logo_path) return null;
  return await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = fileUrl(company.logo_path);
  });
}

export const HOME_TYPES = [
  { id: "single_family", label: "Casa única" },
  { id: "townhouse", label: "Sobrado" },
  { id: "apartment", label: "Apartamento" },
  { id: "other", label: "Outro" },
];

export const ROOMS = [
  { id: "kitchen", label: "Cozinha" },
  { id: "family_room", label: "Sala de família" },
  { id: "living_room", label: "Sala de estar" },
  { id: "dining_room", label: "Sala de jantar" },
  { id: "stairways", label: "Escadas" },
  { id: "attic", label: "Sótão" },
  { id: "basement", label: "Porão" },
  { id: "garage", label: "Garagem" },
  { id: "utility_room", label: "Lavanderia" },
  { id: "office_den", label: "Escritório" },
  { id: "rec_room", label: "Sala de recreação" },
  { id: "hallway", label: "Corredor" },
  { id: "other", label: "Outro" },
];

export const CLEANING_TYPES = [
  { id: "standard", label: "Padrão" },
  { id: "deep", label: "Profunda" },
  { id: "party_special", label: "Festa / Ocasião especial" },
  { id: "window", label: "Janelas" },
  { id: "move_in_out", label: "Mudança (entrada/saída)" },
  { id: "other", label: "Outro" },
];

export const FREQUENCIES = [
  { id: "one_time", label: "Única vez" },
  { id: "weekly", label: "Semanal" },
  { id: "bi_monthly", label: "Quinzenal" },
  { id: "monthly", label: "Mensal" },
  { id: "twice_a_month", label: "2x por mês" },
  { id: "seasonally", label: "Sazonal" },
  { id: "annually", label: "Anual" },
  { id: "other", label: "Outro" },
];

export const PREFERRED_DAYS = [
  { id: "mon", label: "Seg" },
  { id: "tue", label: "Ter" },
  { id: "wed", label: "Qua" },
  { id: "thu", label: "Qui" },
  { id: "fri", label: "Sex" },
  { id: "sat", label: "Sáb" },
  { id: "sun", label: "Dom" },
];

export const PREFERRED_TIMES = [
  { id: "8_11", label: "8h – 11h" },
  { id: "11_4", label: "11h – 16h" },
  { id: "4_7", label: "16h – 19h" },
];

export const ADDITIONAL_SERVICES = [
  { id: "oven_cleaning", label: "Limpeza de forno" },
  { id: "refrigerator", label: "Geladeira" },
  { id: "wall_washing", label: "Lavagem de paredes" },
  { id: "mini_blinds", label: "Persianas" },
  { id: "light_fixtures", label: "Luminárias" },
  { id: "floor_waxing", label: "Encerar piso" },
];

export const STATUS_INFO = {
  open: { label: "Em aberto", classes: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Aprovado", classes: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejeitado", classes: "bg-red-50 text-red-700 border-red-200" },
};

function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "_____________";
  return `$ ${Number(v).toFixed(2)}`;
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US");
  } catch {
    return s;
  }
}

function labelFor(list, id) {
  return list.find((x) => x.id === id)?.label || id;
}

function joinSelected(list, ids, otherText) {
  const names = (ids || []).map((id) => labelFor(list, id));
  if (otherText && names.includes("Other")) {
    return names
      .filter((n) => n !== "Other")
      .concat([`Other: ${otherText}`])
      .join(", ");
  }
  return names.join(", ") || "—";
}

/**
 * Gera PDF do orçamento — layout em uma única página A4 com checkboxes
 * marcados nas opções selecionadas (formato similar ao formulário em papel).
 */
export async function exportEstimatePDF(est, lang = "pt") {
  const _dict = lang === "en" ? EN : lang === "es" ? ES : null;
  const tr = (s) => (_dict ? _dict[s] ?? s : s);
  const company = await getCompanySettings();
  const logoData = await _loadLogoForEstimate(company);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  let y = 12;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(30, 64, 175);
  const companyName = (company?.name || "Clean Manager USA").toUpperCase();
  let nameX = 12;
  if (logoData) {
    try {
      doc.addImage(logoData, "JPEG", 12, y - 4, 12, 12);
      nameX = 26;
    } catch (e) {
      logError("Estimate logo failed:", e);
    }
  }
  doc.text(companyName, nameX, y + 2);
  doc.setFontSize(13);
  doc.setTextColor(15, 23, 42);
  doc.text(tr("Formulário de orçamento").toUpperCase(), W - 12, y + 2, { align: "right" });
  y += 7;

  // Contact line
  const contact = companyContactLines(company).join(" · ");
  if (contact) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(contact, 12, y);
    y += 4;
  }

  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(0.8);
  doc.line(12, y, W - 12, y);
  y += 6;

  // ── Two-column header: CLIENT (left) and QUOTE INFO (right) ────────────
  const boxTop = y;
  const boxH = 36;
  const gutter = 4;
  const colW = (W - 24 - gutter) / 2;
  const leftX = 12;
  const rightX = leftX + colW + gutter;

  // Subtle background fill for each card
  doc.setFillColor(248, 250, 252); // slate-50
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.setLineWidth(0.3);
  doc.roundedRect(leftX, boxTop, colW, boxH, 2, 2, "FD");
  doc.roundedRect(rightX, boxTop, colW, boxH, 2, 2, "FD");

  // ── Left card: CLIENTE ────────────────────────────────────────────────
  const labelColor = [100, 116, 139]; // slate-500
  const textColor = [15, 23, 42];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(30, 64, 175);
  doc.text(tr("Cliente").toUpperCase(), leftX + 4, boxTop + 5);

  // small divider under title
  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(0.5);
  doc.line(leftX + 4, boxTop + 6, leftX + 18, boxTop + 6);

  // helper for a label/value row inside a card
  function field(x, baseY, label, value) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...labelColor);
    doc.text(label.toUpperCase(), x, baseY);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...textColor);
    const lines = doc.splitTextToSize(String(value || "—"), colW - 8);
    doc.text(lines[0] || "—", x, baseY + 4);
  }

  let leftY = boxTop + 11;
  field(leftX + 4, leftY, tr("Nome"), est.name);
  field(leftX + 4 + colW / 2, leftY, tr("Telefone"), est.phone);
  leftY += 9;
  field(leftX + 4, leftY, tr("Endereço"), est.address);
  leftY += 9;
  const petsLabel =
    est.pets === "yes"
      ? tr("Sim")
      : est.pets === "no"
        ? tr("Não")
        : "—";
  field(leftX + 4, leftY, tr("Pets"), petsLabel);
  field(leftX + 4 + colW / 2, leftY, tr("Nome do(s) Pet(s)"), est.pet_names);

  // ── Right card: DADOS DO ORÇAMENTO ────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(30, 64, 175);
  doc.text(tr("Dados do orçamento").toUpperCase(), rightX + 4, boxTop + 5);
  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(0.5);
  doc.line(rightX + 4, boxTop + 6, rightX + 38, boxTop + 6);

  let rightY = boxTop + 11;
  const estimateNumber =
    est.number || est.id?.toString().slice(-6).toUpperCase() || "—";
  field(rightX + 4, rightY, tr("Nº do orçamento"), `#${estimateNumber}`);
  field(rightX + 4 + colW / 2, rightY, tr("Status"), tr(STATUS_INFO[est.status]?.label) || est.status || "—");
  rightY += 9;
  field(rightX + 4, rightY, tr("Data"), fmtDate(est.date));
  field(rightX + 4 + colW / 2, rightY, tr("Preparado por"), est.prepared_by);
  rightY += 9;
  field(rightX + 4, rightY, tr("Validade"), est.valid_until ? fmtDate(est.valid_until) : "—");
  field(rightX + 4 + colW / 2, rightY, tr("E-mail"), est.email);

  y = boxTop + boxH + 5;

  // ── Rates card (full width) ──────────────────────────────────────────
  const ratesH = 22;
  doc.setFillColor(239, 246, 255); // blue-50
  doc.setDrawColor(191, 219, 254); // blue-200
  doc.setLineWidth(0.3);
  doc.roundedRect(12, y, W - 24, ratesH, 2, 2, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(30, 64, 175);
  doc.text(tr("Tarifas propostas").toUpperCase(), 16, y + 5);
  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(0.5);
  doc.line(16, y + 6, 50, y + 6);

  const rateItems = [
    { label: tr("Semanal"), value: est.rate_weekly },
    { label: tr("Quinzenal"), value: est.rate_bimonthly },
    { label: tr("Mensal"), value: est.rate_monthly },
    { label: tr("Outra"), value: est.rate_other },
  ];
  const colWR = (W - 24 - 8) / 4;
  rateItems.forEach((r, i) => {
    const x = 16 + i * colWR;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(r.label.toUpperCase(), x, y + 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(fmtMoney(r.value), x, y + 18);
  });

  y += ratesH + 6;

  // Helper to render a section title
  function section(title) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30, 64, 175);
    doc.text(title, 12, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(15, 23, 42);
  }

  // Helper to render a checkbox row across N columns
  function checkboxes(items, selectedIds, cols = 3, otherText, otherKey = "other") {
    const colW = (W - 24) / cols;
    let col = 0;
    const startY = y;
    items.forEach((it) => {
      const x = 14 + col * colW;
      const lineY = startY + Math.floor(col / cols + items.indexOf(it) / cols) * 4.6;
      const rowY = startY + Math.floor(items.indexOf(it) / cols) * 4.6;
      const isOn = (selectedIds || []).includes(it.id);
      doc.setDrawColor(15, 23, 42);
      doc.setLineWidth(0.3);
      doc.rect(x, rowY - 3, 3, 3);
      if (isOn) {
        doc.setLineWidth(0.6);
        doc.line(x + 0.4, rowY - 2.5, x + 2.6, rowY - 0.5);
        doc.line(x + 0.4, rowY - 0.5, x + 2.6, rowY - 2.5);
        doc.setLineWidth(0.3);
      }
      let label = tr(it.label);
      if (it.id === otherKey && isOn && otherText) {
        label = `${tr(it.label)}: ${otherText}`;
      }
      doc.text(label, x + 5, rowY);
      col = (col + 1) % cols;
      void lineY;
    });
    const rows = Math.ceil(items.length / cols);
    y = startY + rows * 4.6 + 4;
  }

  section(tr("Tipo de imóvel") + ":");
  // Render home types in a row of 4 + bedrooms/bathrooms/sqft inline below
  checkboxes(HOME_TYPES, est.home_types, 4, est.home_type_other);
  doc.text(`${tr("Nº de Quartos")}: ${est.bedrooms ?? "—"}`, 14, y);
  doc.text(`${tr("Nº de Banheiros")}: ${est.bathrooms ?? "—"}`, 80, y);
  doc.text(`${tr("Metragem (sqft)")}: ${est.square_footage ?? "—"}`, 145, y);
  y += 7;

  section(tr("Cômodos a serem limpos") + ":");
  checkboxes(ROOMS, est.rooms, 3, est.rooms_other);

  section(tr("Tipo de limpeza") + ":");
  checkboxes(CLEANING_TYPES, est.cleaning_types, 3, est.cleaning_type_other);

  section(tr("Frequência") + ":");
  checkboxes(FREQUENCIES, est.frequencies, 4, est.frequency_other);

  section(tr("Dias preferidos") + ":");
  checkboxes(PREFERRED_DAYS, est.preferred_days, 7);

  section(tr("Horário preferido") + ":");
  checkboxes(PREFERRED_TIMES, est.preferred_times, 3);

  doc.setFont("helvetica", "normal");
  doc.text(
    `${tr("Você fornecerá os produtos de limpeza?")} ${est.provides_products === "yes" ? `[X] ${tr("Sim")}  [ ] ${tr("Não")}` : est.provides_products === "no" ? `[ ] ${tr("Sim")}  [X] ${tr("Não")}` : `[ ] ${tr("Sim")}  [ ] ${tr("Não")}`}`,
    12,
    y,
  );
  y += 7;

  section(tr("Serviços adicionais") + ":");
  checkboxes(ADDITIONAL_SERVICES, est.additional_services, 3);

  // Key instructions + signature
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 64, 175);
  doc.text(tr("Instruções da chave / acesso") + ":", 12, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  const lines = doc.splitTextToSize(est.key_instructions || "—", W - 24);
  lines.slice(0, 4).forEach((ln) => {
    doc.text(ln, 14, y);
    y += 4;
  });
  y += 6;

  doc.setFont("helvetica", "bold");
  doc.text(tr("Assinatura").toUpperCase() + ":", 12, y);
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(0.3);
  doc.line(38, y + 0.5, W - 12, y + 0.5);
  y += 8;

  // Footer
  doc.setDrawColor(30, 64, 175);
  doc.setLineWidth(2);
  doc.line(12, 287, W - 12, 287);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(71, 85, 105);
  doc.text(`${tr("Status")}: ${tr(STATUS_INFO[est.status]?.label) || est.status} · ${tr("gerado em")} ${new Date().toLocaleString("en-US")}`, 12, 292);

  const safeName = (est.name || "estimate").replace(/[^a-zA-Z0-9-]/g, "_");
  doc.save(`estimate-${safeName}-${Date.now()}.pdf`);

  // mark joinSelected as used (also exposed for UI summary)
  void joinSelected;
}

export { joinSelected };
